import { ManualIconCellRenderer } from "@/controls/ag-grid/components/ButtonCellRenderer";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { IconButton, Tooltip } from "@mui/material";
import { AddCircleOutlineOutlined as AddCircleOutlineOutlinedIcon } from "@mui/icons-material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { WarningAmber as WarningAmberIcon } from "@mui/icons-material";
import {
  CellStyle,
  ICellRendererParams,
  IHeaderGroupParams,
  RowClassParams,
  RowStyle,
  ValueGetterParams,
} from "ag-grid-community";
import { IHeaderParams } from "ag-grid-community";
import { ColDef, ColGroupDef } from "ag-grid-enterprise";
import { useMemo, useState } from "react";
import { ChemistryResult } from "../../../../models/lab.models";

const CustomHeader = (props: IHeaderParams): JSX.Element => {
  return (
    <div
      style={{
        fontSize: "0.8rem",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
      }}
    >
      {props.displayName}
    </div>
  );
};

interface CustomHeaderGroupProps extends IHeaderGroupParams {
  setShowModal: (value: boolean) => void;
  pause: () => void;
  addNewSample?: boolean;
  gradeId: number | null;
  setError: (message: string | null) => void;
}

const CustomHeaderGroup = (props: CustomHeaderGroupProps): JSX.Element => {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        position: "relative",
      }}
    >
      {/* Add Sample Button - Left */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        {props.addNewSample && (
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <IconButton
              color="primary"
              onClick={() => {
                if (props.gradeId === null) {
                  props.setError("Please select a grade first.");
                } else {
                  props.pause();
                  props.setShowModal(true);
                }
              }}
              title={`Add Sample`}
              style={{ padding: "6px" }}
            >
              <AddCircleOutlineOutlinedIcon
                style={{ cursor: "pointer", fontSize: "22px" }}
              />
            </IconButton>
            <span
              style={{
                fontSize: "0.7rem",
                fontWeight: "normal",
              }}
            >
              Add Sample
            </span>
          </div>
        )}
      </div>

      {/* Title - Center */}
      <span style={{ color: "black", fontSize: "0.8rem" }}>
        {props.displayName}
      </span>

      {/* Legends - Right */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <div
            style={{
              width: "12px",
              height: "12px",
              backgroundColor: "#cce5ff",
              marginRight: "4px",
            }}
          />
          <span style={{ fontSize: "0.7rem", fontWeight: "normal" }}>
            Below MIN
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div
            style={{
              width: "12px",
              height: "12px",
              backgroundColor: "#f8d7da",
              marginRight: "4px",
            }}
          />
          <span style={{ fontSize: "0.7rem", fontWeight: "normal" }}>
            Above MAX
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div
            style={{
              width: "12px",
              height: "12px",
              backgroundColor: "#fff3cd",
              marginRight: "4px",
            }}
          />
          <span style={{ fontSize: "0.7rem", fontWeight: "normal" }}>
            Duplicate
          </span>
        </div>
      </div>
    </div>
  );
};

const commonCellStyle: CellStyle = {
  fontSize: "0.8rem",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

interface UseTableDataReturn {
  columns: ColGroupDef[];
  chemistriesColDef: ColDef;
  elementColumnsForWizard: ColDef[];
  getRowStyle: (params: RowClassParams) => RowStyle;
  error: string | null;
  setError: (message: string | null) => void;
}

interface UseTableDataProps {
  chemistries?: ChemistryResult[];
  editable: boolean;
  setShowModal: (value: boolean) => void;
  pause: () => void;
  addNewSample: boolean;
  setOpenConfirmModal: (value: boolean) => void;
  gradeId: number | null;
}

export const useTableData = ({
  chemistries = [],
  editable,
  setShowModal,
  pause,
  addNewSample,
  setOpenConfirmModal,
  gradeId,
}: UseTableDataProps): UseTableDataReturn => {
  const [error, setError] = useState<string | null>(null);

  const getRowStyle = (params: RowClassParams): RowStyle => {
    if (params.data && ["MIN", "MAX"].includes(params.data.Sample)) {
      return {
        backgroundColor: "#f0f0f0",
      };
    }
    if (params.data?.IsDuplicate) {
      return {
        backgroundColor: "#fff3cd",
      };
    }
    return {};
  };

  const regularElementKeys = [
    "C",
    "Mn",
    "S",
    "P",
    "Si",
    "Cr",
    "Ni",
    "Mo",
    "Cu",
    "V",
    "Sn",
    "Nb",
    "N",
    "Al",
    "B",
    "Ti",
    "Zr",
    "Pb",
    "FEPercentage",
    "CE",
    "Corr",
    "CEA6",
    "CE55",
    "CE31",
  ];

  const dynamicElementFields = useMemo(() => {
    if (!chemistries || chemistries.length === 0) {
      return regularElementKeys;
    }

    const orderMap: Record<string, number> = {};
    const fields = new Set<string>();

    chemistries.forEach((chem) => {
      const { ElementName, Order } = chem;
      Object.keys(chem).forEach((key) => {
        if (
          ![
            "Sample",
            "SampleSequence",
            "SampleTypeName",
            "ChemistrySampleId",
            "Reported",
            "ValueType",
            "MinValue",
            "MaxValue",
            "IsManual",
            "IsDuplicate",
          ].includes(key) &&
          !key.endsWith("_Status") &&
          !key.endsWith("Id")
        ) {
          fields.add(key);
        }
      });

      if (ElementName !== undefined && Order !== undefined) {
        if (!orderMap[ElementName]) {
          orderMap[ElementName] = Order;
        }
      }
    });

    let arrayOfFields = Array.from(fields);
    arrayOfFields.sort((a, b) => {
      const orderA = orderMap[a] ?? Number.POSITIVE_INFINITY;
      const orderB = orderMap[b] ?? Number.POSITIVE_INFINITY;
      return orderA - orderB;
    });

    return arrayOfFields;
  }, [chemistries]);

  const HEADER_CHAR_WIDTH = 8;
  const HEADER_PADDING = 16;
  const MIN_ELEMENT_WIDTH = 48;

  const getElementMinWidth = (name: string): number =>
    Math.max(
      MIN_ELEMENT_WIDTH,
      name.length * HEADER_CHAR_WIDTH + HEADER_PADDING,
    );

  const elementColumns: ColDef[] = dynamicElementFields.map((field) => {
    const headerName = field === "FEPercentage" ? "FE%" : field;
    return {
      field,
      headerName,
      headerComponent: CustomHeader,
      flex: 1,
      minWidth: getElementMinWidth(headerName),
      cellStyle: (params) => {
        const status = params.data?.[`${field}_Status`];
        switch (status) {
          case "Above":
            return {
              ...commonCellStyle,
              backgroundColor: "#f8d7da",
            };
          case "Below":
            return {
              ...commonCellStyle,
              backgroundColor: "#cce5ff",
            };
          case "Within":
          default:
            return { ...commonCellStyle, backgroundColor: "", fontWeight: "" };
        }
      },
      editable: (params) => {
        const sample = params?.data?.Sample;
        return (
          !["MIN", "MAX"].includes(sample?.trim().toUpperCase() ?? "") &&
          editable
        );
      },
      valueGetter: (params: ValueGetterParams) => {
        return params?.data?.[field];
      },
    };
  });

  const columns: ColGroupDef[] = [
    {
      headerName: "Chemistry",
      headerGroupComponent: (params: IHeaderGroupParams) => (
        <CustomHeaderGroup
          {...params}
          setShowModal={setShowModal}
          pause={pause}
          addNewSample={addNewSample}
          gradeId={gradeId}
          setError={setError}
        />
      ),
      children: [
        {
          field: "Sample",
          headerName: "Sample",
          headerComponent: CustomHeader,
          width: 65,
          editable: false,
          cellRenderer: (params: ICellRendererParams) => {
            if (!params.value) return null;
            if (params.data?.IsDuplicate) {
              return (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <Tooltip title="Duplicate sample detected" arrow>
                    <WarningAmberIcon
                      style={{
                        fontSize: "16px",
                        color: "#856404",
                      }}
                    />
                  </Tooltip>
                  <span>{params.value}</span>
                </div>
              );
            }
            return params.value;
          },
        },
        {
          field: "Time",
          headerName: "Time",
          headerComponent: CustomHeader,
          width: 55,
          valueGetter: (params: any) => {
            if (params.data?.Reported) {
              return dxtToLocalServerTime(params.data.Reported, "HH:mm");
            }
            return "";
          },
          editable: false,
        },
        ...elementColumns,
        ...(addNewSample
          ? [
              {
                field: "IsManual",
                headerName: "",
                type: ["nonSorteable", "nonFilterable", "centerAligned"],
                cellRenderer: ManualIconCellRenderer,
                cellClass: "ag-button-cell",
                editable: false,
                width: 30,
              },
              {
                field: "actions",
                headerName: " ",
                width: 40,
                editable: false,
                cellRenderer: (params: ICellRendererParams) => {
                  if (
                    params.data.Sample === "MIN" ||
                    params.data?.Sample === "MAX"
                  ) {
                    return null;
                  }
                  return (
                    <div className="ag-grid-buttons">
                      <IconButton
                        color="secondary"
                        onClick={() => {
                          pause();
                          setOpenConfirmModal(true);
                        }}
                        title="Delete"
                      >
                        <DeleteIcon
                          style={{ cursor: "pointer" }}
                          fontSize="small"
                        />
                      </IconButton>
                    </div>
                  );
                },
              },
            ]
          : []),
      ],
    },
  ];

  const elementColumnsForWizard: ColDef[] = useMemo(() => {
    const fieldsToUse =
      !chemistries || chemistries.length === 0
        ? regularElementKeys
        : dynamicElementFields;
    return fieldsToUse.map((field) => ({
      field,
      headerName: field === "FEPercentage" ? "FE%" : field,
      headerComponent: CustomHeader,
      flex: 1,
      cellStyle: commonCellStyle,
      editable: true,
      valueGetter: (params: ValueGetterParams) => {
        return params?.data?.[field];
      },
      cellEditor: "agNumberCellEditor",
      cellEditorParams: {
        min: 0,
        max: 99.9999,
        step: 0.0001,
        precision: 4,
        showStepperButtons: true,
      },
    }));
  }, [chemistries, dynamicElementFields, regularElementKeys]);

  const chemistriesColDef: ColDef = {
    filter: false,
    floatingFilter: false,
    sortable: false,
    resizable: false,
    cellStyle: commonCellStyle,
    singleClickEdit: true,
    suppressMovable: true,
  };

  return {
    columns,
    chemistriesColDef,
    elementColumnsForWizard,
    getRowStyle,
    error,
    setError,
  };
};
